import React from "react";
import MUIDataTable from "mui-datatables";

import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from "@material-ui/core/styles";

import { THEME_COLOR } from "./../../config/config";

// Applied to any column with `options.stickyRight: true`: the cell stays
// pinned to the right edge while the rest of the table scrolls under it.
// Scoped to td/th because mui-datatables puts the cell class on the <td> AND
// on an inner div (same rule as DataTable.js).
const useStyles = makeStyles(() => ({
  stickyRight: {
    "td&, th&": {
      position: "sticky",
      right: 0,
      zIndex: 2,
      backgroundColor: "#fff",
    },
    "th&": { zIndex: 3 },
    "tr:hover > td&": { backgroundColor: "#f5f5f5" },
  },
}));

const theme = createTheme({
  shadows: Array(5).fill("none"),
  root: {
    "& MuiInputLabel-outlined": {
      zIndex: 0,
    },
    "& MuiTableHead": {
      zIndex: 0,
    },
  },
  overrides: {
    MUIDataTableHeadCell: {
      fixedHeader: {
        zIndex: 1, // must be HIGH
        backgroundColor: "#fff",
      },
    },
    MuiTableFooter: {
      root: {
        background: "#f0f2f4",
      },
    },
  },
  MuiFormLabel: {
    zIndex: 0,
  },
  MuiTableHead: { zIndex: 0 },
  palette: {
    primary: {
      main: THEME_COLOR,
    },
  },
});

export default function DataTableExtended(props) {
  const classes = useStyles();
  let responsive = "standard";
  let tableBodyHeight = props.tableBodyHeight || "400px";
  let tableBodyMinHeight = "200px";

  const pad = (n) => String(n).padStart(2, "0");

  const getDateFormats = (date) => {
    const M = date.getMonth() + 1;
    const D = date.getDate();
    const Y = date.getFullYear();
    const MM = pad(M);
    const DD = pad(D);
    const months = [
      "january", "february", "march", "april", "may", "june",
      "july", "august", "september", "october", "november", "december",
    ];
    const monthsShort = [
      "jan", "feb", "mar", "apr", "may", "jun",
      "jul", "aug", "sep", "oct", "nov", "dec",
    ];
    const mFull = months[date.getMonth()];
    const mShort = monthsShort[date.getMonth()];

    return [
      `${MM}-${DD}-${Y}`,       // 03-12-2026  (USA with leading zeros)
      `${M}-${D}-${Y}`,         // 3-12-2026
      `${MM}/${DD}/${Y}`,       // 03/12/2026  (USA with leading zeros)
      `${M}/${D}/${Y}`,         // 3/12/2026
      `${DD}/${MM}/${Y}`,       // 12/03/2026  (EU format)
      `${Y}-${MM}-${DD}`,       // 2026-03-12  (ISO)
      `${mFull} ${D}, ${Y}`,    // march 12, 2026
      `${mFull} ${DD}, ${Y}`,   // march 12, 2026
      `${mShort} ${D}, ${Y}`,   // mar 12, 2026
      `${mShort} ${DD} ${Y}`,   // mar 12 2026
      `${D} ${mShort} ${Y}`,    // 12 mar 2026
      `${DD} ${mShort} ${Y}`,   // 12 mar 2026
      mFull,                    // march  (month name alone)
      mShort,                   // mar
      String(Y),                // 2026  (year alone)
      `${MM}${DD}${Y}`,         // 03122026  (all digits)
      `${M}${D}${Y}`,           // 3122026
    ];
  };

  const options = {
    filter: true,
    responsive,
    tableBodyHeight,
    tableBodyMinHeight,
    selectableRows: "none",
    rowsPerPage: 50,
    rowsPerPageOptions: [25, 50, 100],
    jumpToPage: true,
    rowHover: true,
    customSearch: (searchQuery, currentRow, columns) => {
      const q = searchQuery.toLowerCase().trim();
      if (!q) return true;

      for (let i = 0; i < currentRow.length; i++) {
        const value = currentRow[i];
        const colName = columns[i]?.name || "";

        if (colName === "dateCreated" && value) {
          const date = new Date(value);
          if (!isNaN(date)) {
            const formats = getDateFormats(date);

            // Full / partial text match against every format
            if (formats.some((f) => f.toLowerCase().includes(q))) return true;

            // Digit-strip fallback: "03-12-2" → "03122" matches "03122026"
            const qDigits = q.replace(/[^0-9]/g, "");
            if (qDigits.length >= 3) {
              const dateDigits = `${pad(date.getMonth() + 1)}${pad(date.getDate())}${date.getFullYear()}`;
              if (dateDigits.includes(qDigits)) return true;
            }
          }
        }

        // Standard search for all other columns
        if (value !== null && value !== undefined) {
          if (value.toString().toLowerCase().includes(q)) return true;
        }
      }

      return false;
    },
    // Allow a page to supply extra MUIDataTable options (e.g. columnOrder)
    // without redefining the shared defaults above.
    ...(props.options || {}),
  };

  const columns = (props.columns || []).map((column) =>
    column && column.options && column.options.stickyRight
      ? {
          ...column,
          options: {
            ...column.options,
            setCellProps: () => ({ className: classes.stickyRight }),
            setCellHeaderProps: () => ({ className: classes.stickyRight }),
          },
        }
      : column,
  );

  return (
    <ThemeProvider theme={theme}>
      <div style={{ isolation: "isolate" }}>
        <MUIDataTable
          title={props.title}
          data={props.tableData}
          columns={columns}
          options={options}
        />
      </div>
    </ThemeProvider>
  );
}